import React, { useCallback, useEffect, useState } from 'react';

const CONFETTI_COLORS = ['#7C3AED', '#A78BFA', '#10B981', '#F59E0B', '#EC4899', '#3B82F6'];
const PIECE_COUNT = 60;
const DURATION_MS = 2800;

type ConfettiPiece = {
  id: number;
  x: number;
  color: string;
  size: number;
  delay: number;
  drift: number;
  spin: number;
};

const generatePieces = (): ConfettiPiece[] =>
  Array.from({ length: PIECE_COUNT }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    color: CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
    size: 6 + Math.random() * 7,
    delay: Math.random() * 0.4,
    drift: (Math.random() - 0.5) * 120,
    spin: Math.random() * 720 - 360,
  }));

export const useConfetti = () => {
  const [pieces, setPieces] = useState<ConfettiPiece[]>([]);

  const launch = useCallback(() => {
    setPieces(generatePieces());
  }, []);

  useEffect(() => {
    if (pieces.length === 0) return;
    const timer = setTimeout(() => setPieces([]), DURATION_MS);
    return () => clearTimeout(timer);
  }, [pieces]);

  return { pieces, launch };
};

export const Confetti: React.FC<{ pieces: ConfettiPiece[] }> = ({ pieces }) => {
  if (pieces.length === 0) return null;

  return (
    <>
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(-20px) translateX(0px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) translateX(var(--confetti-drift)) rotate(var(--confetti-spin)); opacity: 0; }
        }
      `}</style>
      {pieces.map((p) => (
        <div
          key={p.id}
          className="pointer-events-none fixed top-0"
          style={{
            left: `${p.x}%`,
            width: p.size,
            height: p.size * 0.55,
            background: p.color,
            borderRadius: 2,
            zIndex: 999,
            ['--confetti-drift' as string]: `${p.drift}px`,
            ['--confetti-spin' as string]: `${p.spin}deg`,
            animation: `confettiFall 2.4s ease-in ${p.delay}s forwards`,
          }}
        />
      ))}
    </>
  );
};
